import {
  getLocalStorageValue,
  setLocalStorageValue,
  UserType,
} from "@xompass/web-sdk";

export const HOST_INIT_MESSAGE = "HOST_INIT";
export const CHILD_READY_MESSAGE = "CHILD_READY";
export const HOST_BOOTSTRAP_MESSAGE = "HOST_BOOTSTRAP";
export const APP_READY_MESSAGE = "APP_READY";

export type IFrameMessage = {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
  source?: string;
};

export type AuthPayload = {
  token: string;
  userId: string;
  userType: UserType;
  varId?: string;
};

export type BootstrapPayload = {
  id?: string;
  auth?: AuthPayload;
  token?: string;
  userId?: string;
  userType?: string;
  varId?: string;
  language?: string;
  theme?: string;
  preferences?: {
    language?: string;
    theme?: string;
  };
  source?: string;
};

type BootstrapMessage = {
  type?: string;
  payload?: BootstrapPayload;
  source?: string;
};

let origin = "";
let sourceId = "";
let readySentForSource = "";
let appReady = false;
let registeredHandler: ((event: MessageEvent) => void) | null = null;

export function normalizeUserType(value: string | null): UserType | "" {
  if (
    value === "Admin" ||
    value === "Manager" ||
    value === "SuperAdmin" ||
    value === "SupportAdmin"
  ) {
    return value;
  }
  return "";
}

function normalizeAuthPayload(payload: BootstrapPayload): AuthPayload | null {
  const authPayload = payload.auth || {
    token: payload.token,
    userId: payload.userId,
    userType: payload.userType,
    varId: payload.varId,
  };

  if (!authPayload?.token || !authPayload?.userId || !authPayload?.userType) {
    return null;
  }

  const userType = normalizeUserType(authPayload.userType);
  if (!userType) {
    return null;
  }

  return {
    token: authPayload.token,
    userId: authPayload.userId,
    userType,
    varId: authPayload.varId,
  };
}

export function persistAuthPayload(payload: AuthPayload) {
  setLocalStorageValue("vsaas$accessToken", payload.token, 10 * 60);
  setLocalStorageValue("vsaas$userId", payload.userId, 10 * 60);
  setLocalStorageValue("vsaas$principalType", payload.userType, 10 * 60);

  if (
    payload.userType === "Admin" ||
    payload.userType === "SuperAdmin" ||
    payload.userType === "SupportAdmin"
  ) {
    if (payload.varId != null) {
      setLocalStorageValue("vsaas$varId", payload.varId);
    }
  } else {
    setLocalStorageValue("vsaas$varId", null);
  }
}

export function persistBootstrapPayload(
  payload: BootstrapPayload,
): AuthPayload | null {
  const language = payload.language || payload.preferences?.language;
  if (language) {
    setLocalStorageValue("globalLanguage", language);
  }

  const theme = payload.theme || payload.preferences?.theme;
  if (theme) {
    setLocalStorageValue("globalTheme", theme);
  }

  const authPayload = normalizeAuthPayload(payload);
  if (!authPayload) {
    return null;
  }

  persistAuthPayload(authPayload);
  return authPayload;
}

function handleHostInit(eventOrigin: string, id: string) {
  const isSameSession = origin === eventOrigin && sourceId === id;
  origin = eventOrigin;
  sourceId = id;
  setLocalStorageValue("sourceId", sourceId);
  if (isSameSession && readySentForSource === sourceId) {
    return;
  }

  readySentForSource = sourceId;
  window.parent?.postMessage(
    { type: CHILD_READY_MESSAGE, source: sourceId },
    origin,
  );

  if (appReady) {
    sendMessageToParent({
      type: APP_READY_MESSAGE,
    });
  }
}

function getAllowedOrigins() {
  const origins = import.meta.env.VITE_PARENT_ORIGINS;
  if (!origins) {
    return [];
  }

  const allowedOrigins = origins
    .split(",")
    .map((value: string) => value.trim())
    .filter(Boolean);

  const currentDomain = window.location.origin;
  if (!allowedOrigins.includes(currentDomain)) {
    allowedOrigins.push(currentDomain);
  }

  return allowedOrigins;
}

export function registerIframeListeners(): () => void {
  if (!window.parent) {
    return () => null;
  }

  const allowedOrigins = getAllowedOrigins();

  if (registeredHandler) {
    window.removeEventListener("message", registeredHandler);
  }

  const handler = (event: MessageEvent) => {
    if (!allowedOrigins.includes(event.origin)) {
      console.log("Received message from unallowed origin", event.origin);
      return;
    }

    const { type, payload } = event.data as BootstrapMessage;

    if (!type) {
      return;
    }
    switch (type) {
      case HOST_INIT_MESSAGE: {
        const id = payload?.id;
        if (id) {
          handleHostInit(event.origin, id);
        }
        break;
      }
      case HOST_BOOTSTRAP_MESSAGE: {
        if (origin && event.origin !== origin) {
          return;
        }

        const source =
          (event.data as BootstrapMessage).source || payload?.source;
        if (sourceId && source && source !== sourceId) {
          return;
        }

        persistBootstrapPayload(payload || {});
        break;
      }
      default:
        console.log(type, payload);
    }
  };

  window.addEventListener("message", handler);
  registeredHandler = handler;

  return () => {
    window.removeEventListener("message", handler);
    if (registeredHandler === handler) {
      registeredHandler = null;
    }
  };
}

export function sendMessageToParent(message: IFrameMessage) {
  const parent = window.parent;
  if (!parent || !origin) {
    console.warn("No parent window or origin to send message to", parent);
    return;
  }

  message.source = getLocalStorageValue("sourceId");

  parent.postMessage(message, origin);
}

export function notifyHostAppReady() {
  appReady = true;
  if (!origin || !sourceId) {
    return;
  }

  sendMessageToParent({
    type: APP_READY_MESSAGE,
  });
}

export function waitForBootstrapFromParent(
  timeoutMs: number,
): Promise<BootstrapPayload | null> {
  if (!window.parent) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener("message", onMessage);
      resolve(null);
    }, timeoutMs);

    const onMessage = (event: MessageEvent) => {
      const data = event.data as BootstrapMessage;
      if (!data?.type) {
        return;
      }

      if (data.type === HOST_INIT_MESSAGE) {
        const initPayload = data.payload || {};
        if (!initPayload.id) {
          return;
        }

        handleHostInit(event.origin, initPayload.id);
        return;
      }

      if (data.type !== HOST_BOOTSTRAP_MESSAGE) {
        return;
      }

      if (origin && event.origin !== origin) {
        return;
      }

      const source = data.source || data.payload?.source;
      if (sourceId && source && source !== sourceId) {
        return;
      }

      const payload = data.payload || {};
      const authPayload = normalizeAuthPayload(payload);
      if (!authPayload) {
        return;
      }

      if (!settled) {
        persistBootstrapPayload(payload);
        settled = true;
        window.clearTimeout(timeoutId);
        window.removeEventListener("message", onMessage);
        resolve({
          ...payload,
          auth: authPayload,
          token: authPayload.token,
          userId: authPayload.userId,
          userType: authPayload.userType,
          varId: authPayload.varId,
        });
      }
    };

    window.addEventListener("message", onMessage);
  });
}
